'use client'

import React, { createContext, useContext, ReactNode, useEffect, useState, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import { getSession } from '@/lib/auth-client'
import { User } from '@/lib/auth'
import { logger } from '@/lib/logger'

interface AuthContextType {
	user: User | null
	isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * AuthProvider - Provides authentication context to client components
 * 
 * For server components, use `getUser()` from '@/lib/auth-session' directly
 * 
 * @example
 * // In client components:
 * const { user, isLoading } = useAuth()
 * 
 * // In server components:
 * const user = await getUser()
 */
export const AuthProvider = ({ children, initialUser }: { children: ReactNode, initialUser?: User | null }) => {
	const [mounted, setMounted] = useState(false)
	const [sessionUser, setSessionUser] = useState<User | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const pathname = usePathname()
	
	const fetchSession = useCallback(() => {
		setIsLoading(true)
		
		// Fetch session using client API (not useSession hook to avoid router context issues)
		getSession()
			.then((result) => {
				// getSession returns { data: { user, session } } or error
				if ('data' in result && result.data?.user) {
					setSessionUser(result.data.user as User)
				} else {
					setSessionUser(null)
				}
				setIsLoading(false)
			})
			.catch((error) => {
				logger.error('Error fetching session', error)
				setSessionUser(null)
				setIsLoading(false)
			})
	}, [])
	
	// Only fetch session on client after mount to avoid SSR/router context issues
	useEffect(() => {
		setMounted(true)
		fetchSession()
	}, [fetchSession])
	
	// Refresh session on route changes (e.g., after logout redirect)
	useEffect(() => {
		if (mounted) {
			fetchSession()
		}
	}, [pathname, mounted, fetchSession])
	
	// Priority: session user (after mount) > initialUser > null
	// During SSR and initial render, use initialUser
	const user = mounted && sessionUser ? sessionUser : (initialUser ?? null)
	const finalIsLoading = mounted ? isLoading : false

	return (
		<AuthContext.Provider value={{ user, isLoading: finalIsLoading }}>
			{children}
		</AuthContext.Provider>
	)
}

export const useAuth = () => {
	const context = useContext(AuthContext)
	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider')
	}
	return context
}
